import { toNestErrors } from '@hookform/resolvers';
import { cloudResourceIdSiteSeparationRuleSchemaZ } from '@ipfabric/utils/schema/siteSeparation/cloudResourceIdSiteSeparationRule';
import { cloudResourceTagSiteSeparationRuleSchemaZ } from '@ipfabric/utils/schema/siteSeparation/cloudResourceTagSiteSeparationRule';
import { regexSiteSeparationRuleSchemaZ } from '@ipfabric/utils/schema/siteSeparation/regexSiteSeparationRule';
import { routingSwitchingSiteSeparationRuleSchemaZ } from '@ipfabric/utils/schema/siteSeparation/routingSwitchingSiteSeparationRule';
import type { SiteSeparationRule } from '@ipfabric/utils/schema/siteSeparation/siteSeparationRule';
import { slugSiteSeparationRuleSchemaZ } from '@ipfabric/utils/schema/siteSeparation/slugSiteSeparationRule';
import type { FieldError, Resolver } from 'react-hook-form';
import type { RuleEditorValues } from './RuleEditor.types';
import {
  createRuleFromEditorValues,
  isTestRuleAvailable,
} from './ruleEditorValues';
import { validateRegexRule } from './validateRegexRule';

const ruleEditorValidationFields = [
  'type',
  'note',
  'regex',
  'siteName',
  'transformation',
  'applyToCloudInstances',
  'minimumSiteSize',
  'firewall',
  'wan',
  'enabledVendors',
  'tagNames',
] as const satisfies ReadonlyArray<keyof RuleEditorValues>;

export const GENERIC_VALIDATION_ERROR =
  'Rule is invalid. Please review the rule settings.';

const VALIDATION_RULE_ID = '11111111-1111-4111-8111-111111111111';

export const SHARED_RULE_ERROR_FIELD = 'root.sharedRule';

const getSharedSchemaForRule = (rule: SiteSeparationRule) => {
  switch (rule.type) {
    case 'regexHostname':
    case 'regexSnmpLocation':
      return regexSiteSeparationRuleSchemaZ;
    case 'regexCloudResourceId':
      return cloudResourceIdSiteSeparationRuleSchemaZ;
    case 'cloudResourceTag':
      return cloudResourceTagSiteSeparationRuleSchemaZ;
    case 'routingSwitching':
      return routingSwitchingSiteSeparationRuleSchemaZ;
    case 'slug':
      return slugSiteSeparationRuleSchemaZ;
  }
};

const mapIssuePathToField = (
  path: Array<string | number | symbol>,
): keyof RuleEditorValues | null => {
  const [field] = path;
  if (typeof field !== 'string') {
    return null;
  }
  return (
    ruleEditorValidationFields.find((ruleField) => ruleField === field) ?? null
  );
};

const addFieldError = (
  errors: Record<string, FieldError>,
  fieldName: string,
  message: string,
) => {
  if (fieldName in errors) {
    return;
  }
  errors[fieldName] = { type: 'manual', message };
};

export const getRuleEditorValidationErrors = (
  values: RuleEditorValues,
  existingRuleId: string | null = VALIDATION_RULE_ID,
): Record<string, FieldError> => {
  const errors: Record<string, FieldError> = {};

  // cloudResourceTag is testable but has no regex/siteName fields, so its empty
  // `regex` must not be validated — validateRegexRule would report "Regular
  // Expression is mandatory" and block saving. Its own zod schema covers it below.
  if (isTestRuleAvailable(values.type) && values.type !== 'cloudResourceTag') {
    const validation = validateRegexRule({
      regex: values.regex,
      siteName: values.siteName,
    });

    if (validation.regex) {
      addFieldError(errors, 'regex', validation.regex);
    }

    if (validation.siteName) {
      addFieldError(errors, 'siteName', validation.siteName);
    }
  }

  const rule = createRuleFromEditorValues(values, existingRuleId ?? undefined);
  const validation = getSharedSchemaForRule(rule).safeParse(rule);

  if (validation.success) {
    return errors;
  }

  for (const issue of validation.error.issues) {
    const fieldName = mapIssuePathToField(issue.path);

    if (!fieldName) {
      addFieldError(errors, SHARED_RULE_ERROR_FIELD, GENERIC_VALIDATION_ERROR);
      continue;
    }

    addFieldError(errors, fieldName, issue.message);
  }

  return errors;
};

export const ruleEditorResolver: Resolver<RuleEditorValues> = (
  values,
  _context,
  options,
) => {
  const errors = getRuleEditorValidationErrors(values);

  if (Object.keys(errors).length === 0) {
    return { values, errors: {} };
  }

  return {
    values: {},
    errors: toNestErrors(errors, options),
  };
};
